import React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

type ScrollDirection = "x" | "y";

type ScrollNodeProps = {
  children?: React.ReactNode;
  direction?: ScrollDirection;
  step?: number;
  showButtons?: boolean;
  leftLabel?: React.ReactNode;
  rightLabel?: React.ReactNode;
  upLabel?: React.ReactNode;
  downLabel?: React.ReactNode;
  sx?: any;
  trackSx?: any;
  contentSx?: any;
  buttonSx?: any;
  startButtonSx?: any;
  endButtonSx?: any;
  hideButtonsAtEdges?: boolean;
};

const scrollbarHiddenSx = {
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
};

function normalizeSx(sx: any) {
  if (!sx || typeof sx !== "object" || Array.isArray(sx)) return sx;
  const next = { ...sx };

  if (next.bgColor !== undefined && next.bgcolor === undefined) {
    next.bgcolor = next.bgColor;
    delete next.bgColor;
  }

  return next;
}

function scrollBy(ref: React.RefObject<HTMLDivElement | null>, direction: ScrollDirection, amount: number) {
  const el = ref.current;
  if (!el) return;

  el.scrollBy({
    left: direction === "x" ? amount : 0,
    top: direction === "y" ? amount : 0,
    behavior: "smooth",
  });
}

export function ScrollNode({
  children,
  direction = "x",
  step = 260,
  showButtons = true,
  leftLabel = "‹",
  rightLabel = "›",
  upLabel = "⌃",
  downLabel = "⌄",
  sx,
  trackSx,
  contentSx,
  buttonSx,
  startButtonSx,
  endButtonSx,
  hideButtonsAtEdges = true,
}: ScrollNodeProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [edge, setEdge] = React.useState({ start: true, end: false, overflow: false });
  const isHorizontal = direction === "x";

  const updateEdge = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const current = isHorizontal ? el.scrollLeft : el.scrollTop;
    const viewport = isHorizontal ? el.clientWidth : el.clientHeight;
    const size = isHorizontal ? el.scrollWidth : el.scrollHeight;
    const max = Math.max(size - viewport, 0);
    const tolerance = 2;

    setEdge({
      start: current <= tolerance,
      end: current >= max - tolerance,
      overflow: max > tolerance,
    });
  }, [isHorizontal]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    updateEdge();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateEdge)
        : null;

    resizeObserver?.observe(el);

    Array.from(el.children).forEach((child) => {
      resizeObserver?.observe(child);
    });

    window.addEventListener("resize", updateEdge);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateEdge);
    };
  }, [children, updateEdge]);

  const hideStartButton = hideButtonsAtEdges && (!edge.overflow || edge.start);
  const hideEndButton = hideButtonsAtEdges && (!edge.overflow || edge.end);

  const baseButtonSx = {
    minWidth: 0,
    width: isHorizontal ? "32px" : "100%",
    height: isHorizontal ? "100%" : "32px",
    borderRadius: 0,
    color: "#aaaaaa",
    fontSize: "34px",
    lineHeight: 1,
    flex: "0 0 auto",
    p: 0,
    ...normalizeSx(buttonSx),
  };

  const trackBaseSx = isHorizontal
    ? {
        minWidth: 0,
        flex: "1 1 auto",
        display: "flex",
        overflowX: "auto",
        overflowY: "hidden",
      }
    : {
        minHeight: 0,
        flex: "1 1 auto",
        overflowY: "auto",
        overflowX: "hidden",
      };

  const contentBaseSx = isHorizontal
    ? {
        display: "inline-flex",
        minWidth: "max-content",
      }
    : {
        display: "flex",
        flexDirection: "column",
      };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isHorizontal ? "row" : "column",
        alignItems: "stretch",
        minWidth: 0,
        minHeight: 0,
        ...normalizeSx(sx),
      }}
    >
      {showButtons && (
        <IconButton
          aria-label={isHorizontal ? "Scroll left" : "Scroll up"}
          onClick={() => scrollBy(ref, direction, -step)}
          sx={{
            ...baseButtonSx,
            visibility: hideStartButton ? "hidden" : "visible",
            ...normalizeSx(startButtonSx),
          }}
        >
          {isHorizontal ? leftLabel : upLabel}
        </IconButton>
      )}

      <Box
        ref={ref}
        onScroll={updateEdge}
        sx={{
          ...trackBaseSx,
          ...scrollbarHiddenSx,
          ...normalizeSx(trackSx),
        }}
      >
        <Box
          sx={{
            ...contentBaseSx,
            ...normalizeSx(contentSx),
          }}
        >
          {children}
        </Box>
      </Box>

      {showButtons && (
        <IconButton
          aria-label={isHorizontal ? "Scroll right" : "Scroll down"}
          onClick={() => scrollBy(ref, direction, step)}
          sx={{
            ...baseButtonSx,
            visibility: hideEndButton ? "hidden" : "visible",
            ...normalizeSx(endButtonSx),
          }}
        >
          {isHorizontal ? rightLabel : downLabel}
        </IconButton>
      )}
    </Box>
  );
}

export function ScrollXNode(props: ScrollNodeProps) {
  return <ScrollNode {...props} direction="x" />;
}

export function ScrollYNode(props: ScrollNodeProps) {
  return <ScrollNode {...props} direction="y" />;
}

export default ScrollNode;
